import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/Layout';
import { FileText, Download, Loader2, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { downloadFileUrl } from '../../lib/download';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Reports = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingPDF, setGeneratingPDF] = useState(null);
  const [generatingCSV, setGeneratingCSV] = useState(null);
  const [generatingProject, setGeneratingProject] = useState(false);
  const [selectedJobs, setSelectedJobs] = useState([]);

  useEffect(() => {
    fetchProject();
    fetchJobs();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`);
      setProject(response.data);
    } catch (error) {
      toast.error('Failed to fetch project');
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/analysis-jobs`);
      setJobs(response.data.filter(j => j.status === 'completed').sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
    } catch (error) {
      toast.error('Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = async (jobId) => {
    setGeneratingPDF(jobId);
    try {
      const response = await axios.post(`${API}/analysis-jobs/${jobId}/report`);
      await downloadFileUrl(response.data.reportUrl, `report_${jobId.substring(0, 8)}.pdf`);
      toast.success('PDF report downloaded!');
    } catch (error) {
      toast.error('Failed to generate PDF');
    } finally {
      setGeneratingPDF(null);
    }
  };

  const generateCSV = async (jobId) => {
    setGeneratingCSV(jobId);
    try {
      const response = await axios.post(`${API}/analysis-jobs/${jobId}/export-csv`);
      await downloadFileUrl(response.data.csvUrl, `measurements_${jobId.substring(0, 8)}.csv`);
      toast.success('CSV exported!');
    } catch (error) {
      toast.error('Failed to export CSV');
    } finally {
      setGeneratingCSV(null);
    }
  };

  const exportAllCSV = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/measurements/export-all-csv`);
      await downloadFileUrl(response.data.csvUrl, 'all_measurements.csv');
      toast.success(`Exported ${response.data.count} measurements!`);
    } catch (error) {
      toast.error('Failed to export all measurements');
    }
  };

  const generateProjectReport = async () => {
    setGeneratingProject(true);
    try {
      const response = await axios.post(
        `${API}/projects/${projectId}/report`,
        { drawings: [] },
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'project_report.pdf';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Project report downloaded');
    } catch (error) {
      toast.error('Failed to generate project report');
    } finally {
      setGeneratingProject(false);
    }
  };

  const getJobTypeLabel = (type) => {
    const labels = {
      'OBJECT_COUNT': 'Object Intelligence',
      'QUALITY_INSPECTION': 'Quality Inspection',
      'COMBINED': 'Combined Analysis',
      'MEASURE_2D': '2D Measurement',
      'CIVIL_SURFACE_3D': '3D Surface',
      'ROOM_3D': 'Room 3D'
    };
    return labels[type] || type;
  };

  const compareJobs = () => {
    if (selectedJobs.length !== 2) {
      toast.error('Please select exactly 2 jobs to compare');
      return;
    }
    const job1 = jobs.find(j => j.id === selectedJobs[0]);
    const job2 = jobs.find(j => j.id === selectedJobs[1]);
    
    if (job1.type !== job2.type) {
      toast.error('Can only compare jobs of same type');
      return;
    }

    // Simple comparison display
    let comparison = '';
    if (job1.type === 'MEASURE_2D') {
      const area1 = job1.result?.area || 0;
      const area2 = job2.result?.area || 0;
      const delta = area2 - area1;
      const deltaPct = area1 ? (delta / area1 * 100).toFixed(1) : 0;
      comparison = `Area Change: ${delta.toFixed(2)} (${deltaPct}%)`;
    } else if (job1.type.includes('3D')) {
      const area1 = job1.result?.totals?.netSurfaceSqM || 0;
      const area2 = job2.result?.totals?.netSurfaceSqM || 0;
      const delta = area2 - area1;
      const deltaPct = area1 ? (delta / area1 * 100).toFixed(1) : 0;
      comparison = `Surface Area Change: ${delta.toFixed(2)} m² (${deltaPct}%)`;
    }
    toast.success(comparison);
  };

  const toggleJobSelection = (jobId) => {
    if (selectedJobs.includes(jobId)) {
      setSelectedJobs(selectedJobs.filter(id => id !== jobId));
    } else if (selectedJobs.length < 2) {
      setSelectedJobs([...selectedJobs, jobId]);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  const sessionGroups = {};
  jobs.forEach((j) => {
    const sid = j.sessionId || (j.drawingLocation && j.drawingLocation.sessionId);
    if (sid) {
      if (!sessionGroups[sid]) sessionGroups[sid] = [];
      sessionGroups[sid].push(j);
    }
  });
  const sessionList = Object.entries(sessionGroups).map(([sid, sjobs]) => {
    let area = 0;
    let unit = 'm';
    sjobs.forEach((j) => {
      area += Number(j.result?.area || 0);
      unit = j.result?.unit || unit;
    });
    return { sid, count: sjobs.length, area: Math.round(area * 100) / 100, unit };
  });

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Reports & Export</h1>
          <p className="text-slate-600 mt-1">{project?.name}</p>
        </div>

        {/* Export All */}
        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Export All Measurements</h2>
              <p className="text-sm text-slate-600 mt-1">Download CSV with all measurement data</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={generateProjectReport} disabled={generatingProject} className="flex items-center gap-2 bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 shadow-md disabled:opacity-50" data-testid="project-report-btn">
                <Download className="w-5 h-5" />
                <span>{generatingProject ? 'Generating…' : 'Project Report (PDF)'}</span>
              </button>
              <button onClick={exportAllCSV} className="flex items-center gap-2 bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 shadow-md" data-testid="export-all-csv-btn">
                <Download className="w-5 h-5" />
                <span>Export All CSV</span>
              </button>
            </div>
          </div>
        </div>

        {/* Progress Comparison */}
        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Progress Comparison</h2>
          <p className="text-sm text-slate-600 mb-4">Select 2 measurements of the same type to compare</p>
          {selectedJobs.length > 0 && (
            <div className="mb-4">
              <p className="text-sm text-blue-600">{selectedJobs.length} job(s) selected</p>
              {selectedJobs.length === 2 && (
                <button onClick={compareJobs} className="mt-2 flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700" data-testid="compare-jobs-btn">
                  <BarChart3 className="w-4 h-4" />
                  <span>Compare</span>
                </button>
              )}
            </div>
          )}
        </div>

        {/* Jobs List */}
        {sessionList.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6 overflow-hidden" data-testid="session-groups">
            <div className="px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">Live Camera Sessions</h2>
              <p className="text-sm text-slate-600">Rooms captured together, with combined floor area</p>
            </div>
            <div className="divide-y divide-slate-200">
              {sessionList.map((s) => (
                <div key={s.sid} className="flex items-center justify-between px-6 py-3" data-testid="session-group-row">
                  <div>
                    <p className="font-medium text-slate-900">Session {s.sid.substring(0, 8)}</p>
                    <p className="text-xs text-slate-500">{s.count} room{s.count !== 1 ? 's' : ''}</p>
                  </div>
                  <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-semibold" data-testid="session-subtotal">
                    Combined {s.area} {s.unit}²
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900">Analysis Reports</h2>
          </div>
          <div className="divide-y divide-slate-200">
            {jobs.map((job) => (
              <div key={job.id} className="p-6 hover:bg-slate-50 transition-colors" data-testid={`job-item-${job.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-3">
                    <input type="checkbox" checked={selectedJobs.includes(job.id)} onChange={() => toggleJobSelection(job.id)} className="mt-1" data-testid={`job-checkbox-${job.id}`} />
                    <div>
                      <h3 className="font-semibold text-slate-900">{getJobTypeLabel(job.type)}</h3>
                      <p className="text-sm text-slate-600 mt-1">{new Date(job.createdAt).toLocaleString()}</p>
                      <p className="text-xs text-slate-500 mt-1">Job ID: {job.id.substring(0, 8)}</p>
                    </div>
                  </div>
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">{job.status}</span>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => generatePDF(job.id)} disabled={generatingPDF === job.id} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50" data-testid={`generate-pdf-${job.id}`}>
                    {generatingPDF === job.id ? <><Loader2 className="w-4 h-4 animate-spin" /><span>Generating...</span></> : <><FileText className="w-4 h-4" /><span>PDF Report</span></>}
                  </button>
                  {(job.type.includes('MEASURE') || job.type.includes('3D')) && (
                    <button onClick={() => generateCSV(job.id)} disabled={generatingCSV === job.id} className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50" data-testid={`export-csv-${job.id}`}>
                      {generatingCSV === job.id ? <><Loader2 className="w-4 h-4 animate-spin" /><span>Exporting...</span></> : <><Download className="w-4 h-4" /><span>Export CSV</span></>}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          {jobs.length === 0 && (
            <div className="text-center py-12 text-slate-500">No completed analyses found</div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Reports;
